import { createStore } from 'zustand'
import { ReadingGoalsInitialState, ReadingGoalsStore } from './types'
import {
  getGoalSuggestions,
  getReadingGoals,
  updateGoalSuggestionPrefs,
  updateStatsGoalOptIn,
} from '../../requests'
import { sendToNative } from 'app/webview/utils'
import { periodTypeParam } from '../../utils'
import { ReadingGoalPeriodType } from '../../types'

export const createReadingGoalsStore = ({
  ...initialState
}: ReadingGoalsInitialState) => {
  return createStore<ReadingGoalsStore>()((set, get) => ({
    ...initialState,
    status: 'loading',
    period: (periodTypeParam as ReadingGoalPeriodType) || 'year',
    goals: [],
    source: 'custom_goals',
    suggestions: [],
    closedSuggestions: [],
    loadingSuggestions: false,
    updateGoalOptIn: async (optIn) => {
      const { statsUserId } = get()

      if (!statsUserId) return

      set({ goalOptIn: optIn })

      try {
        await updateStatsGoalOptIn({ userId: statsUserId, optIn })
      } catch (e) {
        console.error('Error updating stats reading goal opt in', e)
      }
    },
    setPeriod: (period) => set({ period }),
    getReadingGoals: async ({ period, checkAll, year, fetchSuggestions }) => {
      const { statsUserId, clearReadingGoals, getSuggestions } = get()

      set({ status: 'loading' })
      clearReadingGoals()

      if (!checkAll) set({ period })

      try {
        if (!statsUserId.length) {
          throw new Error('Stats user id is missing')
        }

        const goals = await getReadingGoals({
          userId: statsUserId,
          period: checkAll ? undefined : period,
          year,
        })

        if (checkAll) {
          const periodList = goals.map((g) => g.period_type)

          const getDefaultPeriod = (): ReadingGoalPeriodType => {
            if (periodList.includes('week')) return 'week'
            if (periodList.includes('month')) return 'month'
            return 'year'
          }

          const defaultPeriod = getDefaultPeriod()

          const filteredGoals = goals.filter(
            (g) => g.period_type === defaultPeriod
          )

          set({
            period: defaultPeriod,
            goals: filteredGoals,
            status: 'success',
          })

          if (fetchSuggestions) {
            await getSuggestions(defaultPeriod)
          }

          return
        }

        set({
          period,
          goals: goals,
          status: 'success',
        })

        if (fetchSuggestions) {
          await getSuggestions(period)
        }
      } catch (e) {
        const err = e as Error
        console.error('Goals could not be fetched', err.message)
        set({ status: 'error' })
      }
    },
    createReadingGoal: ({ goal_type, period_type, number_goal, year } = {}) => {
      let url = 'https://fable.co/app/reading_goals_create'
      const params = new URLSearchParams()

      if (goal_type) params.set('type', goal_type)
      if (period_type) params.set('period', period_type)
      if (number_goal) params.set('value', number_goal.toString())
      if (year) params.set('year', year.toString())

      if (Array.from(params).length > 0) {
        url += `?${params.toString()}`
      }

      sendToNative({
        id: 'create-reading-goal',
        payload: {
          type: 'deep_link',
          body: {
            url,
            close: true,
          },
        },
      })
    },
    editReadingGoal: (id: string) => {
      sendToNative({
        id: 'edit-reading-goal',
        payload: {
          type: 'deep_link',
          body: {
            url: `https://fable.co/app/reading_goals_edit?goal_id=${id}`,
            close: true,
          },
        },
      })
    },
    closeSuggestion: async (period, goalType) => {
      const { closedSuggestions, statsUserId } = get()

      set({ closedSuggestions: [...closedSuggestions, period] })

      try {
        await updateGoalSuggestionPrefs({
          userId: statsUserId,
          period,
          goalType,
        })
      } catch (e) {
        const err = e as Error
        console.error('Error closing suggestion', err)
      }
    },
    clearReadingGoals: () => set({ goals: [], suggestions: [] }),
    getSuggestions: async (period) => {
      const { statsUserId, isWeb } = get()

      if (isWeb) return

      set({ loadingSuggestions: true })

      try {
        const suggestions = await getGoalSuggestions({
          userId: statsUserId,
          period,
        })

        set({ suggestions })
      } catch (e) {
        const err = e as Error
        console.error('Error fetching goal suggestions', err)
      } finally {
        set({ loadingSuggestions: false })
      }
    },
  }))
}
